import type { Area } from "react-easy-crop";

// A canvas can only re-encode these without losing what makes the file
// useful: SVG would be rasterised and GIF would lose its animation, so the
// other image types upload as they are.
const CROPPABLE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export const isCroppableImage = (file: File): boolean =>
  CROPPABLE_TYPES.has(file.type);

export const formatDimensions = (area: Area): string =>
  `${Math.round(area.width)} x ${Math.round(area.height)} px`;

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load the image"));
    image.src = src;
  });

export async function cropImageFile(
  file: File,
  src: string,
  area: Area
): Promise<File> {
  const image = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(area.width));
  canvas.height = Math.max(1, Math.round(area.height));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is not available");
  context.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    canvas.width,
    canvas.height
  );
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, file.type)
  );
  if (!blob) throw new Error("Could not encode the cropped image");
  return new File([blob], file.name, {
    type: file.type,
    lastModified: Date.now(),
  });
}
